import { format, parse } from 'date-fns';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import DateTimePickerSection from '../../../../components/dateRangePicker/dateTimePickerSection';
import PlanLimitNotice from '../../../../components/dateRangePicker/planLimitNotice';
import { LITE_MAX_EXPORT_DAYS } from '../../../../helpers/dateRangePlanLimits';
import type { RootState } from '../../../../state/store';
import type { ExportDateRange } from '../../types';

type ExportDateRangePickerProps = {
  value: ExportDateRange;
  onChange: (value: ExportDateRange) => void;
};

const ExportDateRangePicker = ({ value, onChange }: ExportDateRangePickerProps) => {
  const { plan_type: planType } = useSelector((state: RootState) => state.company.company_details);

  const [startDate, setStartDate] = useState(() => parse(value.dates[0], 'yyyy-MM-dd', new Date()));
  const [endDate, setEndDate] = useState(() => parse(value.dates[1], 'yyyy-MM-dd', new Date()));
  const [startHour, setStartHour] = useState(value.timeRange[0][0]);
  const [startMinute, setStartMinute] = useState(value.timeRange[0][1]);
  const [endHour, setEndHour] = useState(value.timeRange[1][0]);
  const [endMinute, setEndMinute] = useState(value.timeRange[1][1]);

  useEffect(() => {
    setStartDate(parse(value.dates[0], 'yyyy-MM-dd', new Date()));
    setEndDate(parse(value.dates[1], 'yyyy-MM-dd', new Date()));
    setStartHour(value.timeRange[0][0]);
    setStartMinute(value.timeRange[0][1]);
    setEndHour(value.timeRange[1][0]);
    setEndMinute(value.timeRange[1][1]);
  }, [value.dates, value.timeRange]);

  const emitChange = (
    nextStartDate: Date,
    nextEndDate: Date,
    nextStartHour: number,
    nextStartMinute: number,
    nextEndHour: number,
    nextEndMinute: number
  ) => {
    onChange({
      dates: [format(nextStartDate, 'yyyy-MM-dd'), format(nextEndDate, 'yyyy-MM-dd')],
      timeRange: [
        [nextStartHour, nextStartMinute],
        [nextEndHour, nextEndMinute],
      ],
    });
  };

  const handleStartDateChange = (date: Date) => {
    setStartDate(date);
    emitChange(date, endDate, startHour, startMinute, endHour, endMinute);
  };

  const handleEndDateChange = (date: Date) => {
    setEndDate(date);
    emitChange(startDate, date, startHour, startMinute, endHour, endMinute);
  };

  const handleStartHourChange = (hour: number) => {
    setStartHour(hour);
    emitChange(startDate, endDate, hour, startMinute, endHour, endMinute);
  };

  const handleStartMinuteChange = (minute: number) => {
    setStartMinute(minute);
    emitChange(startDate, endDate, startHour, minute, endHour, endMinute);
  };

  const handleEndHourChange = (hour: number) => {
    setEndHour(hour);
    emitChange(startDate, endDate, startHour, startMinute, hour, endMinute);
  };

  const handleEndMinuteChange = (minute: number) => {
    setEndMinute(minute);
    emitChange(startDate, endDate, startHour, startMinute, endHour, minute);
  };

  return (
    <Wrapper>
      <SectionLabel>Export date range</SectionLabel>
      {planType === 'lite' ? (
        <PlanLimitNotice maxLookbackDays={LITE_MAX_EXPORT_DAYS} mode="export" />
      ) : null}
      <PickerContainer>
        <DateTimePickerSection
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleStartDateChange}
          setEndDate={handleEndDateChange}
          startHour={startHour}
          setStartHour={handleStartHourChange}
          startMinute={startMinute}
          setStartMinute={handleStartMinuteChange}
          endHour={endHour}
          setEndHour={handleEndHourChange}
          endMinute={endMinute}
          setEndMinute={handleEndMinuteChange}
          layout="stacked"
          compact
        />
      </PickerContainer>
    </Wrapper>
  );
};

export default ExportDateRangePicker;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const PickerContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border_color};
  border-radius: 12px;
  overflow: visible;
  background: ${({ theme }) => theme.widget?.background || theme.body};
  padding: 12px;
`;
